import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ExternalLink, RefreshCw, Wallet } from "lucide-react";
import { fmt, inr } from "./format";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

/** Prepaid ledger for one vendor — none of them publish a balance API, so this
 *  is an estimate the founder corrects from the vendor's own console. */
export default function WalletBlock({ vendor, onReload, autoFocus = false }) {
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const wallet = vendor.wallet;

  useEffect(() => {
    if (autoFocus && inputRef.current) inputRef.current.focus();
  }, [autoFocus]);

  if (!wallet) {
    return (
      <div
        className="rounded-lg border border-dashed border-slate-200 px-3 py-2.5 text-xs text-slate-400"
        data-testid={`vendor-${vendor.id}-no-wallet`}
      >
        {vendor.billing} — nothing to top up.
      </div>
    );
  }

  const low = wallet.tracked && wallet.balance_inr <= wallet.low_threshold_inr;
  const staleDays = wallet.synced_at
    ? Math.floor((Date.now() - new Date(wallet.synced_at).getTime()) / 86400000)
    : null;

  const post = async (path, body, okMsg) => {
    setBusy(true);
    try {
      const res = await axios.post(
        `${API_URL}/dashboard/services/providers/${vendor.id}/${path}`,
        body,
      );
      toast.success(`${okMsg} — about ${res.data?.wallet?.lookups_left ?? 0} lookups left`);
      setAmount("");
      await onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update the wallet");
    } finally {
      setBusy(false);
    }
  };

  const sync = () => {
    const balance = Number(amount);
    if (amount === "" || !(balance >= 0)) return toast.error("Enter the balance from the console");
    post("balance", { balance_inr: balance }, `Synced to ₹${balance.toLocaleString("en-IN")}`);
  };

  const add = () => {
    const value = Number(amount);
    if (!value || value <= 0) return toast.error("Enter the amount you just recharged");
    post("topup", { amount_inr: value }, `Added ₹${value.toLocaleString("en-IN")}`);
  };

  return (
    <div
      className={`rounded-lg border px-3 py-2.5 ${low ? "border-amber-300 bg-amber-50/70" : "border-slate-200 bg-slate-50/60"}`}
      data-testid={`vendor-${vendor.id}-wallet`}
    >
      <div className="flex items-baseline gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">
            Outstanding balance
          </div>
          <div
            className={`text-2xl font-bold leading-tight ${low ? "text-amber-700" : "text-slate-900"}`}
            data-testid={`vendor-${vendor.id}-balance`}
          >
            {wallet.tracked ? `₹${fmt(Math.round(wallet.balance_inr))}` : "—"}
          </div>
        </div>
        <div className="pl-3 border-l border-slate-200">
          <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">
            Lookups left
          </div>
          <div className="text-lg font-semibold text-slate-700">
            {wallet.tracked ? fmt(wallet.lookups_left) : "—"}
          </div>
        </div>
        <div className="ml-auto flex items-end gap-1.5">
          <input
            type="number"
            min="0"
            placeholder="₹ balance"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            ref={inputRef}
            className="w-24 px-2 py-1.5 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-200"
            data-testid={`vendor-${vendor.id}-wallet-input`}
          />
          <button
            onClick={sync}
            disabled={busy}
            title="Set the ledger to the balance shown in the vendor console"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-slate-800 hover:bg-slate-900 rounded-md disabled:opacity-50 transition-colors"
            data-testid={`vendor-${vendor.id}-wallet-set`}
          >
            {busy ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Wallet className="w-3 h-3" />}
            Set
          </button>
          <button
            onClick={add}
            disabled={busy}
            title="Add a recharge you just made"
            className="px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-white rounded-md border border-slate-200 disabled:opacity-50"
            data-testid={`vendor-${vendor.id}-wallet-add`}
          >
            + Add
          </button>
        </div>
      </div>

      {low && (
        <p className="mt-2 text-[11px] font-medium text-amber-800" data-testid={`vendor-${vendor.id}-low`}>
          Under the ₹{fmt(wallet.low_threshold_inr)} floor — the founders are being WhatsApped daily
          until it is topped up.
        </p>
      )}
      {staleDays !== null && staleDays >= 7 && (
        <p className="mt-2 text-[11px] text-slate-500" data-testid={`vendor-${vendor.id}-stale`}>
          Last synced {staleDays} days ago. Anything spent inside the vendor console never reaches
          this ledger — paste the console figure and hit Set.
        </p>
      )}
      <p className="mt-1.5 text-[11px] text-slate-400">
        {wallet.synced_at
          ? `Synced at ₹${fmt(Math.round(wallet.synced_balance_inr || 0))}, ₹${inr(wallet.spent_since_sync_inr)} spent since`
          : wallet.last_topup_at
            ? `Top-up recorded ₹${fmt(Math.round(wallet.last_topup_inr || 0))}`
            : "No balance recorded yet"}
        {vendor.console_url && (
          <>
            {" · "}
            <a
              href={vendor.console_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-0.5 text-slate-500 hover:text-emerald-700 hover:underline"
              data-testid={`vendor-${vendor.id}-console`}
            >
              console <ExternalLink className="w-2.5 h-2.5" />
            </a>
          </>
        )}
      </p>
    </div>
  );
}
